// The one class component: React's error boundary contract has no hook form.
// A render that throws — a hook outside its provider, a shape the server was
// not expected to send — used to blank the whole document (#187). Now the
// page says so in its own words, and a widget that throws takes only its own
// card down. Nothing is reported by itself (§22 is Pulse's): the error is
// kept in memory for the person's own in-app report, which they read whole and
// send or not (ADR-0518 4a). Nothing is retried, and no stack is drawn on the
// page: the error's message and, when the thrown value carries one, its
// request id.
import { Component, type ReactNode } from "react";
import { isAvailable, noteError, openReport } from "../report/capture";

interface Props {
  children: ReactNode;
  /** What to show in place of the children. */
  fallback: (failure: Failure) => ReactNode;
  /** A change of key forgets the error: a page boundary resets on navigation. */
  resetKey?: unknown;
}

export interface Failure { message: string; requestId?: string }

/** What a thrown value says about itself, and no more. */
export function describeThrown(error: unknown): Failure {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "The page could not be drawn.";
  const requestId = error !== null && typeof error === "object" && "request_id" in error && typeof error.request_id === "string" ? error.request_id : undefined;
  return { message: message || "The page could not be drawn.", requestId };
}

export class Boundary extends Component<Props, { failure?: Failure; key?: unknown }> {
  state: { failure?: Failure; key?: unknown } = {};

  static getDerivedStateFromError(error: unknown) {
    return { failure: describeThrown(error) };
  }

  /** Kept in memory for a person's in-app report and sent only if they send
   * one (ADR-0518 4a): the first of the five events that set the report
   * control's quiet mark. */
  componentDidCatch(error: unknown) {
    noteError("boundary", error);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.failure && prev.resetKey !== this.props.resetKey) this.setState({ failure: undefined });
  }

  render() {
    return this.state.failure ? this.props.fallback(this.state.failure) : this.props.children;
  }
}

/** A page that threw: a sentence, the message, the request id when there is
 * one, and a way back. The link is a plain anchor on purpose — it reloads,
 * which is the recovery a broken page needs, and it depends on nothing that
 * may itself be what threw. */
export function PageFailed({ message, requestId }: Failure) {
  return (
    <div role="alert" className="problem">
      <div>
        <strong>Something went wrong drawing this page.</strong>
        <div className="problem-detail">{message}</div>
        {requestId && <div className="problem-id">request {requestId}</div>}
        <p style={{ marginTop: "var(--sp-3)" }}>
          <a href="/">Back to the overview</a>
          {/* The fallback offers the report already describing this failure
              (ADR-0518 4a), where the deployment relays one. */}
          {isAvailable() && <>{" · "}<button type="button" className="btn btn-sm btn-quiet" onClick={openReport}>Report this</button></>}
        </p>
      </div>
    </div>
  );
}

/** A widget that threw: its card says so; the grid around it is untouched. */
export function WidgetFailed({ message }: Failure) {
  return <p className="widget-failed"><strong>Could not draw this widget.</strong><span>{message}</span></p>;
}
